import React, { useState, useEffect, useCallback } from 'react';
import { AuthService, UserService } from '../Entities/AuthService';
import { useUsers } from '../hooks/useUsers';
import io from 'socket.io-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { Badge } from '@/Components/ui/badge';
import { Alert, AlertDescription } from '@/Components/ui/alert';
import { useToast } from '@/Components/ui/use-toast';
import { 
  Users, 
  UserPlus, 
  Search, 
  Filter, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Shield, 
  ShieldCheck,
  UserCheck,
  UserX,
  Crown,
  Eye,
  AlertCircle,
  CheckCircle,
  RotateCcw,
  Power,
  PowerOff,
  ChevronLeft,
  ChevronRight,
  Mail
} from 'lucide-react';
import UserForm from '../Components/users/UserForm';
import UserStats from '../Components/users/UserStats';
import OptionsManagement from '../Components/users/OptionsManagement';
import { ConfirmationDialog } from '../Components/ui/confirmation-dialog';

export default function UsersPage() {
  const { toast } = useToast();
  const [stats, setStats] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users'); // 'users' ou 'options'
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const itemsPerPage = 10;

  // Estados para modais de confirmação
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: null,
    variant: 'default',
    confirmText: 'Confirmar',
  });

  // Preparar filtros para React Query
  const queryFilters = {
    search: searchTerm || undefined,
    role: roleFilter || undefined,
    status: statusFilter || undefined,
  };

  // Usar hook customizado com React Query
  const {
    data: users,
    total: totalUsers,
    totalPages,
    isLoading,
    isFetching,
    prefetchNextPage,
    prefetchPreviousPage,
    invalidateCache,
  } = useUsers(queryFilters, currentPage, itemsPerPage);

  const currentUser = AuthService.getCurrentUser();

  // Verificar permissões
  useEffect(() => {
    if (!AuthService.canManageUsers()) {
      setError('Sem permissão para gerenciar usuários');
      return;
    }
  }, []);

  // Prefetch automático da próxima página
  useEffect(() => {
    if (!isLoading && users && users.length > 0) {
      prefetchNextPage();
    }
  }, [currentPage, isLoading, users, prefetchNextPage]);

  const loadStats = useCallback(async () => {
    try {
      const response = await UserService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  }, []);

  useEffect(() => {
    if (AuthService.canManageUsers()) {
      loadStats();
    }
  }, [loadStats]);

  // Reset para página 1 quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter]);

  // Socket.io - Atualização em tempo real
  useEffect(() => {
    // ✅ Em produção: usa mesma URL do frontend (Nginx faz proxy)
    // ✅ Em desenvolvimento: usa porta 3010 diretamente
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const serverUrl = isDevelopment 
      ? `${window.location.protocol}//${window.location.hostname}:3010`
      : window.location.origin; // Usa mesma origem (Nginx faz proxy)
    
    const socket = io(serverUrl, {
      path: '/socket.io' // ✅ Caminho do Socket.IO (Nginx faz proxy)
    });

    socket.on('connect', () => {
      // Conectado ao servidor
    });

    socket.on('data_updated', (update) => {
      
      if (update.type === 'user_created' || update.type === 'user_updated' || update.type === 'user_deleted') {
        invalidateCache();
        loadStats();
      }
    });

    // 🔔 Listener para verificação de email em tempo real
    socket.on('user_verified', (data) => {
      
      // Mostrar notificação toast
      toast({
        title: "✅ Conta Ativada!",
        description: `${data.name || data.email} verificou o email e ativou a conta com sucesso.`,
        className: "bg-green-50 border-green-200",
      });
      
      // Atualizar lista de usuários
      invalidateCache();
      loadStats();
    });

    return () => {
      socket.disconnect();
    };
  }, []); // ✅ Array vazio - conecta apenas uma vez

  // Funções de paginação
  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Prefetch páginas adjacentes
    setTimeout(() => {
      prefetchNextPage();
      prefetchPreviousPage();
    }, 100);
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleUserFormClose = () => {
    setShowUserForm(false);
    setEditingUser(null);
  };

  const handleUserFormSuccess = (message) => {
    setSuccess(message);
    setShowUserForm(false);
    setEditingUser(null);
    invalidateCache();
    loadStats();
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleToggleStatus = async (user) => {
    try {
      const response = await UserService.toggleStatus(user.id);
      setSuccess(response.message);
      invalidateCache();
      loadStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleToggleUserManagement = async (user) => {
    try {
      const response = await UserService.toggleUserManagement(user.id);
      setSuccess(response.message);
      invalidateCache();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteUser = (user) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Excluir Usuário',
      description: `Tem certeza que deseja excluir o usuário ${user.name}? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          const response = await UserService.delete(user.id);
          setSuccess(response.message);
          invalidateCache();
          loadStats();
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(err.message);
          setTimeout(() => setError(''), 5000);
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const handleRestoreUser = (user) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Restaurar Usuário',
      description: `Deseja restaurar o usuário ${user.name}? O usuário voltará a ter acesso ao sistema.`,
      confirmText: 'Restaurar',
      variant: 'default',
      onConfirm: async () => {
        try {
          const response = await UserService.restore(user.id);
          setSuccess(response.message);
          invalidateCache();
          loadStats();
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(err.message);
          setTimeout(() => setError(''), 5000);
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const handleResendVerificationEmail = (user) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reenviar Email de Verificação',
      description: `Deseja reenviar o email de verificação para ${user.name} (${user.email})? Um novo link de ativação será enviado.`,
      confirmText: 'Reenviar',
      variant: 'default',
      onConfirm: async () => {
        try {
          const response = await AuthService.resendVerificationEmail(user.id);
          setSuccess(response.message || 'Email de verificação reenviado com sucesso');
          setTimeout(() => setSuccess(''), 5000);
        } catch (err) {
          setError(err.message || 'Erro ao reenviar email de verificação');
          setTimeout(() => setError(''), 5000);
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Administrador';
      case 'user': return 'Usuário';
      case 'observer': return 'Observador';
      default: return role;
    }
  };

  const getRoleBadge = (user) => {
    if (user.is_super_admin || user.role === 'super_admin') {
      return <Badge className="bg-purple-100 text-purple-800 border-purple-200"><Crown className="w-3 h-3 mr-1" />Super Admin</Badge>;
    }
    if (user.role === 'admin') {
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
    }
    if (user.role === 'observer') {
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200"><Eye className="w-3 h-3 mr-1" />Observador</Badge>;
    }
    return <Badge variant="outline">Usuário</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Ativo</Badge>;
      case 'inactive':
        return <Badge className="bg-red-100 text-red-800 border-red-200"><UserX className="w-3 h-3 mr-1" />Inativo</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><AlertCircle className="w-3 h-3 mr-1" />Pendente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const canManageUser = (user) => {
    // Super admin nunca pode ser gerenciado por outros
    if (user.is_super_admin || user.role === 'super_admin') {
      return false;
    }
    
    // Usuário atual é super admin
    if (AuthService.isSuperAdmin()) {
      return true;
    }
    
    // ✅ CORRIGIDO: Administradores podem gerenciar usuários normais e observers
    if (AuthService.isAdmin() && (user.role === 'user' || user.role === 'observer')) {
      return true;
    }
    
    // Usuário com permissão pode gerenciar usuários normais
    return AuthService.canManageUsers() && user.role === 'user';
  };

  if (!AuthService.canManageUsers()) {
    return (
      <div className="p-6">
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700">
            Você não tem permissão para acessar esta página.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Usuários</h1>
          <p className="text-gray-600">Administre contas de usuário e permissões</p>
        </div>
        
        {(AuthService.isAdmin() || AuthService.isSuperAdmin()) && activeTab === 'users' && (
          <Button onClick={handleCreateUser} className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Novo Usuário
          </Button>
        )}
      </div>

      {/* Tabs (apenas para SuperAdmin) */}
      {AuthService.isSuperAdmin() && (
        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'users'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Usuários
            </div>
          </button>
          <button
            onClick={() => setActiveTab('options')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'options'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Opções Dinâmicas
            </div>
          </button>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">{success}</AlertDescription>
        </Alert>
      )}

      {/* Conteúdo baseado na aba ativa */}
      {activeTab === 'options' ? (
        <OptionsManagement />
      ) : (
        <>
          {/* Estatísticas */}
          <UserStats stats={stats} />

          {/* Filtros */}
          <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos os roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="user">Usuário</SelectItem>
                <SelectItem value="observer">Observador</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos os status</SelectItem>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('');
                setStatusFilter('');
              }}
            >
              Limpar Filtros
            </Button>
          </div>
          <div className="mt-4 text-sm text-slate-600">
            Total: {totalUsers} usuários
          </div>
        </CardContent>
      </Card>

      {/* Lista de Usuários */}
      <div className="relative">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-sm font-medium text-slate-700">
                {users && users.length > 0 ? `Carregando página ${currentPage}...` : 'Carregando...'}
              </p>
            </div>
          </div>
        )}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Usuários (Página {currentPage} de {totalPages})
            </CardTitle>
          </CardHeader>
          <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : !users || users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Nenhum usuário encontrado
              </h3>
              <p className="text-gray-500">
                Não há usuários que correspondam aos filtros aplicados.
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {users.map((user) => (
                <div key={user.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border rounded-lg hover:bg-gray-50 gap-3 sm:gap-4">
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-semibold text-sm sm:text-base">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{user.name}</h3>
                        {getRoleBadge(user)}
                        {user.can_manage_users && (
                          <Badge variant="outline" className="text-[10px] sm:text-xs whitespace-nowrap">
                            <ShieldCheck className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                            Pode Gerenciar
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{user.email}</p>
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1">
                        {getStatusBadge(user.status)}
                        {!user.email_verified && (
                          <Badge variant="outline" className="text-[10px] sm:text-xs text-orange-600 whitespace-nowrap">
                            Email não verificado
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 justify-end sm:justify-start">
                    {canManageUser(user) && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                        >
                          <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleStatus(user)}
                          title={user.status === 'active' ? 'Desativar usuário' : 'Ativar usuário'}
                          className={`h-8 w-8 sm:h-9 sm:w-9 p-0 ${user.status === 'active' ? 'text-green-600 hover:text-green-700' : 'text-gray-600 hover:text-gray-700'}`}
                        >
                          {user.status === 'active' ? <Power className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <PowerOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                        </Button>

                        {(user.status === 'pending' || !user.email_verified) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResendVerificationEmail(user)}
                            title="Reenviar email de verificação"
                            className="text-orange-600 hover:text-orange-700 h-8 w-8 sm:h-9 sm:w-9 p-0"
                          >
                            <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        )}

                        {AuthService.isSuperAdmin() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleUserManagement(user)}
                            title="Gerenciar permissões"
                            className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                          >
                            <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        )}

                        {AuthService.isSuperAdmin() && user.deleted_at && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreUser(user)}
                            className="text-blue-600 hover:text-blue-700 h-8 w-8 sm:h-9 sm:w-9 p-0"
                            title="Restaurar usuário"
                          >
                            <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        )}

                        {!user.deleted_at && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-700 h-8 w-8 sm:h-9 sm:w-9 p-0"
                            title="Excluir usuário"
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {!isLoading && totalPages > 1 && (
        <div className="flex flex-col items-center gap-4 mt-6">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevious}
              disabled={currentPage === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Anterior</span>
            </Button>

            <div className="flex items-center gap-1">
              {currentPage > 3 && (
                <>
                  <Button
                    variant={1 === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(1)}
                    className="w-10 h-10"
                  >
                    1
                  </Button>
                  {currentPage > 4 && <span className="px-2 text-slate-400">...</span>}
                </>
              )}

              {Array.from({ length: totalPages || 1 }, (_, i) => i + 1)
                .filter(page => page >= currentPage - 2 && page <= currentPage + 2)
                .map(page => (
                  <Button
                    key={page}
                    variant={page === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(page)}
                    className="w-10 h-10"
                  >
                    {page}
                  </Button>
                ))}

              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && <span className="px-2 text-slate-400">...</span>}
                  <Button
                    variant={totalPages === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(totalPages)}
                    className="w-10 h-10"
                  >
                    {totalPages}
                  </Button>
                </>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNext}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1"
            >
              <span className="hidden sm:inline">Próximo</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="text-sm text-slate-600 text-center">
            Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, totalUsers || 0)} de {totalUsers || 0} usuários
          </div>
        </div>
      )}
      </div>

      {/* Modal de Formulário */}
      {showUserForm && (
        <UserForm
          user={editingUser}
          onClose={handleUserFormClose}
          onSuccess={handleUserFormSuccess}
        />
      )}

      {/* Modal de Confirmação */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
      />
        </>
      )}
    </div>
  );
}
